import { Component } from '@felicity/vision';
import {
  CriterionWeightManager,
  GenericFrameworkSelector,
  NodeWeightManager,
  SystemLevel,
  ToolManager,
  ToolOwnerManager,
  ToolValidationManager,
  TernaryInput
} from '@felicity/multi-criteria-analysis';
import SlidingPanelLayout from '../ui/SlidingPanelLayout.mjs';
import OwnerSelector from '../ui/OwnerSelector.mjs';

/**
 * Assessment Tools screen
 */
export default class AssessmentTools extends Component {
  /**
   * The languages supported by this screen
   * @type {string[]}
   */
  languages = [];

  /**
   * Whether owner management features should be enabled
   * @type {boolean}
   * @default false
   */
  ownerManagement = false;

  /**
   * Provides access to the sliding panel layout
   * @type {SlidingPanelLayout}
   * @readonly
   */
  get slidingPanelLayout() {
    return this.#slidingPanelLayout;
  }

  /** @type {SlidingPanelLayout} */
  #slidingPanelLayout;

  constructor() {
    super();

    this.#slidingPanelLayout = new SlidingPanelLayout();
  }

  _prepare() {
    super._prepare();

    this.appendChild(this.slidingPanelLayout);
    this.classList.add('screen');

    const toolManager = new ToolManager();
    toolManager.languages = this.languages;
    toolManager.crudUri = '/api/MCA/Tool';
    toolManager.autoBind = true;

    toolManager.urlTemplateOf('export', '/api/MCA/Tool/${toolId}/Export'); // eslint-disable-line no-template-curly-in-string
    toolManager.urlTemplateOf('clone', '/api/MCA/Tool/${toolId}/Clone'); // eslint-disable-line no-template-curly-in-string
    if (this.ownerManagement) {
      toolManager.ownerManagement = true;
      toolManager.toolOwnerSelectorClass = OwnerSelector;
    }

    this.slidingPanelLayout.addPanel('Assessment Tools', toolManager);

    toolManager.grid.showColumns('toolId', 'field');
    toolManager.grid.height = '100%';
    toolManager.grid.width = '100%';

    toolManager.addEventListener('open', (e) => {
      this.slidingPanelLayout.clear();

      const tool = e.detail.rowData,
        assessmentSystem = tool.genericFramework?.assessmentSystem;

      if (!assessmentSystem) {
        return;
      }

      assessmentSystem.levels
        .sort((a, b) => a.rank - b.rank)
        .map((level) => SystemLevel.create(level, this.languages))
        .forEach((level) => {
          const nodeWeightManager = this.#createNodeWeightManager(tool, level),
            panel = this.slidingPanelLayout.addPanel(level.name, nodeWeightManager),

            criterionManagers = this.#createCriterionWeightManagers(tool, level),
            contentPanel = panel.querySelector('.panel-content');
          Object.keys(criterionManagers).forEach((ct) => {
            const header = document.createElement('h3');
            header.textContent = ct;
            contentPanel.appendChild(header);
            contentPanel.appendChild(criterionManagers[ct]);
          });

          nodeWeightManager.grid.addEventListener('rowSelected', (e2) => {
            Object.values(criterionManagers).forEach((cwm) => {
              cwm.nodeId = e2.data.nodeId; // eslint-disable-line no-param-reassign
              cwm.enable();
            });
          });
        });

      this.slidingPanelLayout.showPanel(1);
    });
  }

  #createNodeWeightManager(tool, level) {
    const nodeWeightManager = new NodeWeightManager();
    nodeWeightManager.toolId = tool.toolId;
    nodeWeightManager.parentId = level.rank === 1 ? tool.genericFramework?.nodeId : null;
    nodeWeightManager.languages = this.languages;
    nodeWeightManager.crudUri = '/api/MCA/NodeWeight';
    nodeWeightManager.autoBind = level.rank === 1;

    nodeWeightManager.on('open', (e) => {
      const panelIdx = e.detail.rowData.node.level + 1;
      this.#setParentIdToNodeWeightManagerInPanel(panelIdx, e.detail.rowData.node.nodeId);
      this.#enableOpenAction(
        panelIdx,
        e.detail.rowData.node.level + 1 < tool.genericFramework.assessmentSystem.depth
      );
      this.slidingPanelLayout.showPanel(panelIdx);
    });

    return nodeWeightManager;
  }

  #createCriterionWeightManagers(tool, level) {
    const criterionWeightManagers = {};

    level.criterionTypeList.forEach((ct) => {
      const criterionWeightManager = new CriterionWeightManager();
      criterionWeightManager.languages = this.languages;
      criterionWeightManager.crudUri = '/api/MCA/CriterionWeight';
      criterionWeightManager.toolId = tool.toolId;
      criterionWeightManager.criterionType = ct;
      criterionWeightManagers[ct] = criterionWeightManager;
    });

    return criterionWeightManagers;
  }

  #setParentIdToNodeWeightManagerInPanel(panelIdx, parentId) {
    const panel = this.slidingPanelLayout.panels[panelIdx];
    if (!panel) {
      return;
    }
    const nodeWeightManager = panel.querySelector('flc-node-weight-manager');
    if (nodeWeightManager) {
      nodeWeightManager.parentId = parentId || null;
    }
  }

  #enableOpenAction(panelIdx, enable) {
    const panel = this.slidingPanelLayout.panels[panelIdx];
    if (!panel) {
      return;
    }
    const nodeManager = panel.querySelector('flc-node-weight-manager');
    nodeManager?.grid[`${enable ? 'show' : 'hide'}Columns`]('nodeId', 'field');
  }
}

TernaryInput.register('ternary-input');
SlidingPanelLayout.register('sliding-panel-layout');
GenericFrameworkSelector.register('generic-framework-selector');
OwnerSelector.register('owner-selector');
ToolOwnerManager.register('tool-owner-manager');
ToolValidationManager.register('tool-validation-manager');
ToolManager.register('tool-manager');
NodeWeightManager.register('node-weight-manager');
CriterionWeightManager.register('criterion-weight-manager');
